import jsPDF from 'jspdf';
import { PUBLIC_SITE_URL } from '../config/api';

interface TeamMember {
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
}

interface Team {
  id: string;
  name: string;
  category_name?: string;
  members?: TeamMember[];
  /** Code d'équipe = dossard (LOG-172). Chaîne : tous les codes ne sont pas numériques. */
  team_id?: string;
}

interface ExportOptions {
  eventName: string;
  teams: Team[];
  organizationName?: string;
}

/**
 * Service d'export PDF de la liste des équipes
 * Format simple optimisé pour l'impression
 */
export class TeamsPdfService {
  /**
   * Charge le logo Logfit en base64 pour l'inclure dans le PDF
   */
  private static async loadLogo(): Promise<string | null> {
    try {
      const response = await fetch(`${PUBLIC_SITE_URL}/logos/LOGFIT-LOGO-ORANGE.png`);
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.warn('Impossible de charger le logo Logfit:', error);
      return null;
    }
  }

  /**
   * Exporte la liste des équipes au format PDF
   * Groupé par catégorie
   */
  static async exportTeamsList(options: ExportOptions): Promise<void> {
    const { eventName, teams, organizationName } = options;

    // Charger le logo Logfit
    const logoData = await this.loadLogo();

    // Créer le document PDF en format portrait
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Dimensions A4 portrait: 210mm x 297mm
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - 2 * margin;

    let yPosition = margin;

    // === HEADER ===
    const drawHeader = () => {
      // Titre principal
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('LISTE DES ÉQUIPES', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 6;

      // Nom de l'événement
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(eventName, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 5;

      // Organisation
      if (organizationName) {
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        doc.text(organizationName, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 5;
      }

      // Nombre total d'équipes
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(`${teams.length} équipe${teams.length > 1 ? 's' : ''} inscrite${teams.length > 1 ? 's' : ''}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 4;

      // Ligne de séparation
      doc.setDrawColor(180, 180, 180);
      doc.setLineWidth(0.3);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 6;

      doc.setTextColor(0, 0, 0); // Reset couleur texte
    };

    // Dessiner le header de la première page
    drawHeader();

    // === GROUPEMENT PAR CATÉGORIE ===
    const teamsByCategory = new Map<string, Team[]>();

    teams.forEach(team => {
      const categoryKey = team.category_name || 'Sans catégorie';
      if (!teamsByCategory.has(categoryKey)) {
        teamsByCategory.set(categoryKey, []);
      }
      teamsByCategory.get(categoryKey)!.push(team);
    });

    // Trier les catégories alphabétiquement
    const sortedCategories = Array.from(teamsByCategory.keys()).sort();

    // === CONTENU PAR CATÉGORIE ===
    sortedCategories.forEach((categoryName, catIndex) => {
      const teamsInCategory = teamsByCategory.get(categoryName)!;

      // Trier les équipes par nom
      const sortedTeams = [...teamsInCategory].sort((a, b) =>
        a.name.localeCompare(b.name, 'fr')
      );

      // Vérifier si besoin d'une nouvelle page pour le header de catégorie
      if (yPosition + 20 > pageHeight - 20) {
        doc.addPage();
        yPosition = margin + 10;
      }

      // === HEADER DE CATÉGORIE ===
      doc.setFillColor(245, 245, 245);
      doc.setDrawColor(180, 180, 180);
      doc.setLineWidth(0.3);
      doc.rect(margin, yPosition, contentWidth, 7, 'FD');

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 40);
      const categoryHeader = `${categoryName} (${sortedTeams.length} équipe${sortedTeams.length > 1 ? 's' : ''})`;
      doc.text(categoryHeader, margin + 3, yPosition + 5);
      yPosition += 10;

      // === LISTE DES ÉQUIPES ===
      sortedTeams.forEach((team, teamIndex) => {
        // Estimation de la hauteur nécessaire pour cette équipe
        const membersCount = team.members?.length || 0;
        const estimatedHeight = 6 + (membersCount > 0 ? membersCount * 4 : 0);

        // Vérifier si besoin d'une nouvelle page
        if (yPosition + estimatedHeight > pageHeight - 20) {
          doc.addPage();
          yPosition = margin + 10;
        }

        // Nom de l'équipe
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);

        // Ajouter le code d'équipe (= dossard) si disponible
        const teamLabel = team.team_id ? `#${team.team_id} - ${team.name}` : team.name;
        doc.text(teamLabel, margin + 3, yPosition + 4);

        yPosition += 6;

        // Membres de l'équipe (si disponibles)
        if (team.members && team.members.length > 0) {
          team.members.forEach((member, memberIndex) => {
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(80, 80, 80);

            const memberName = `${member.first_name} ${member.last_name}`.trim();
            const details = [member.email, member.phone].filter(Boolean).join(' | ');
            const memberInfo = details ? `${memberName} - ${details}` : memberName;
            doc.text(`  • ${memberInfo}`, margin + 5, yPosition + 3);

            yPosition += 4;
          });
        }

        // Ligne de séparation entre les équipes
        if (teamIndex < sortedTeams.length - 1) {
          doc.setDrawColor(230, 230, 230);
          doc.setLineWidth(0.1);
          doc.line(margin + 3, yPosition + 1, pageWidth - margin - 3, yPosition + 1);
          yPosition += 4;
        } else {
          yPosition += 3;
        }
      });

      // Espace entre les catégories
      if (catIndex < sortedCategories.length - 1) {
        yPosition += 6;
      }
    });

    // === FOOTER (numéro de page + branding) ===
    const totalPages = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);

      // Numéro de page (centre)
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Page ${i} / ${totalPages}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );

      // Branding "Powered by Logfit" (bas droite, discret)
      const brandingY = pageHeight - 9;

      doc.setFontSize(7);
      doc.setTextColor(160, 160, 160);
      const poweredByText = 'Powered by';
      const poweredByWidth = doc.getTextWidth(poweredByText);

      if (logoData) {
        const logoHeight = 4;
        const logoWidth = 12;
        const totalWidth = poweredByWidth + 2 + logoWidth;
        const startX = pageWidth - margin - totalWidth;

        doc.text(poweredByText, startX, brandingY);

        try {
          const logoX = startX + poweredByWidth + 2;
          const logoY = brandingY - logoHeight + 1.5;
          doc.addImage(logoData, 'PNG', logoX, logoY, logoWidth, logoHeight);
        } catch (error) {
          console.warn('Erreur ajout logo au PDF:', error);
          doc.text(' Logfit', startX + poweredByWidth, brandingY);
        }
      } else {
        doc.text(
          'Powered by Logfit',
          pageWidth - margin - 3,
          brandingY,
          { align: 'right' }
        );
      }
    }

    // === TÉLÉCHARGEMENT ===
    const sanitizedEventName = eventName
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]/g, '_')
      .toLowerCase();

    const filename = `equipes_${sanitizedEventName}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
  }
}

export default TeamsPdfService;
